import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useMonth } from '@/contexts/MonthContext';
import { Database, Plus, RefreshCw, Search, Pencil, ChevronDown, X, Calendar } from 'lucide-react';
import LogoSpinner from '@/components/LogoSpinner';
import {
  getGlobalCustomers,
  subscribeToGlobalCustomers,
  createGlobalCustomer,
  updateGlobalCustomer,
  deleteGlobalCustomer,
  getMonthlyUsageForAllGlobalCustomers,
  subscribeToXmPaymentsGlobal,
} from '@/lib/databaseApi';
import type { GlobalPaymentRecord } from '@/lib/databaseApi';
import type { GlobalCustomer, OwnedCard, CardLimitGroup, XmCardBrand } from '@/types/database';
import { CARD_POOL_MAP, CARD_POOL_LIMITS } from '@/types/database';
import { getCardBrandColor } from './customers/cardBrandColors';
import { getMonthName } from '@/lib/dateUtils';
import AddGlobalCustomerModal from './database/AddGlobalCustomerModal';
import EditGlobalCustomerModal from './database/EditGlobalCustomerModal';
import CustomerDetailModal from './database/CustomerDetailModal';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

interface PoolUsage {
  pool: CardLimitGroup;
  limit: number;
  charged: number;
  remaining: number;
  chargesByCompany: { company: string; amount: number }[];
  brands: string[];
}

function computePoolUsage(
  cards: OwnedCard[],
  payments: GlobalPaymentRecord[]
): PoolUsage[] {
  const poolMap = new Map<CardLimitGroup, {
    limit: number;
    charged: number;
    byCompany: Map<string, number>;
    brands: Set<string>;
  }>();

  for (const card of cards) {
    if (!poolMap.has(card.pool)) {
      poolMap.set(card.pool, {
        limit: CARD_POOL_LIMITS[card.pool],
        charged: 0,
        byCompany: new Map(),
        brands: new Set(),
      });
    }
    poolMap.get(card.pool)!.brands.add(card.type);
  }

  for (const p of payments) {
    const pool = CARD_POOL_MAP[p.card_brand as XmCardBrand];
    if (!pool || !poolMap.has(pool)) continue;
    const entry = poolMap.get(pool)!;
    entry.charged += p.charged_amount;
    entry.brands.add(p.card_brand);
    entry.byCompany.set(
      p.business_name,
      (entry.byCompany.get(p.business_name) || 0) + p.charged_amount
    );
  }

  return Array.from(poolMap.entries()).map(([pool, data]) => ({
    pool,
    limit: data.limit,
    charged: data.charged,
    remaining: data.limit - data.charged,
    chargesByCompany: Array.from(data.byCompany.entries())
      .map(([company, amount]) => ({ company, amount }))
      .sort((a, b) => b.amount - a.amount),
    brands: Array.from(data.brands),
  }));
}

const POOL_LABELS: Record<string, string> = {
  debit: 'Debit Pool',
  credit_classic: 'Credit Classic Pool',
  visa_credit_gold: 'Visa Credit Gold',
  master_credit_gold: 'Master Credit Gold',
};

export default function DatabasePage() {
  const now = new Date();
  const { selectedMonth: globalMonth, selectMonth } = useMonth();
  const selectedYear = globalMonth.year;
  const selectedMonth = globalMonth.month;
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  const monthYear = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const monthYearRef = useRef(monthYear);
  monthYearRef.current = monthYear;

  const [customers, setCustomers] = useState<GlobalCustomer[]>([]);
  const [usageMap, setUsageMap] = useState<Map<string, GlobalPaymentRecord[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editCustomer, setEditCustomer] = useState<GlobalCustomer | null>(null);
  const [detailCustomer, setDetailCustomer] = useState<GlobalCustomer | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchAndSet = useCallback(async (my: string) => {
    const [custData, usage] = await Promise.all([
      getGlobalCustomers(),
      getMonthlyUsageForAllGlobalCustomers(my),
    ]);
    if (monthYearRef.current !== my) return;
    setCustomers(custData);
    setUsageMap(usage);
    setLoading(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchAndSet(monthYear).catch(() => {
      if (monthYearRef.current === monthYear) setLoading(false);
    });
  }, [monthYear, fetchAndSet]);

  useEffect(() => {
    const reload = () => {
      const my = monthYearRef.current;
      fetchAndSet(my).catch(() => {});
    };
    const ch1 = subscribeToGlobalCustomers(reload);
    const ch2 = subscribeToXmPaymentsGlobal(reload);

    return () => {
      supabase.removeChannel(ch1);
      supabase.removeChannel(ch2);
    };
  }, [fetchAndSet]);

  function refreshData() {
    setRefreshing(true);
    const my = monthYearRef.current;
    fetchAndSet(my).catch(() => {}).finally(() => setRefreshing(false));
  }

  async function handleAdd(name: string, cards: OwnedCard[], email?: string) {
    try {
      await createGlobalCustomer(name, cards, email);
      setShowAdd(false);
      refreshData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  async function handleEdit(id: string, name: string, cards: OwnedCard[], email?: string) {
    try {
      await updateGlobalCustomer(id, name, cards, email);
      setEditCustomer(null);
      refreshData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteGlobalCustomer(id);
      setEditCustomer(null);
      refreshData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  const enrichedCustomers = useMemo(() => {
    return customers.map(c => {
      const payments = usageMap.get(c.name) || [];
      const pools = computePoolUsage(c.owned_cards, payments);
      const totalCharged = pools.reduce((s, p) => s + p.charged, 0);
      const totalLimit = pools.reduce((s, p) => s + p.limit, 0);
      return { customer: c, payments, pools, totalCharged, totalLimit, totalRemaining: totalLimit - totalCharged };
    });
  }, [customers, usageMap]);

  const filtered = (search.trim()
    ? enrichedCustomers.filter(e => e.customer.name.toLowerCase().includes(search.toLowerCase()))
    : enrichedCustomers
  ).sort((a, b) => b.totalCharged - a.totalCharged);

  const currentYear = now.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear + 1];

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        {searchOpen && (
          <div className="absolute inset-0 z-10 flex items-center px-4 sm:px-6" style={{ background: 'rgba(10, 25, 20, 0.98)' }}>
            <div className="flex-1 flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-3 py-2.5">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search customers..."
                className="flex-1 text-sm text-white bg-transparent outline-none placeholder:text-slate-400"
                autoFocus
              />
              <button
                onClick={() => { setSearch(''); setSearchOpen(false); }}
                className="text-slate-400 hover:text-white transition-colors p-1"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white">Database</h1>
            <p className="text-xs text-white mt-0.5 hidden sm:block">
              {customers.length} customer{customers.length !== 1 ? 's' : ''} &middot; {getMonthName(selectedMonth)} {selectedYear}
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button text-white rounded-xl hover:text-teal-400 text-xs font-medium"
              title="Search customers"
            >
              <Search size={14} />
              <span className="hidden sm:inline">Search</span>
            </button>
            <button
              onClick={() => setMonthDropdownOpen(true)}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button text-white rounded-xl text-xs font-medium"
              title={`${getMonthName(selectedMonth)} ${selectedYear}`}
            >
              <Calendar size={14} className="sm:hidden text-teal-400" />
              <span className="hidden sm:inline">{getMonthName(selectedMonth)} {selectedYear}</span>
              <ChevronDown size={13} className="hidden sm:inline" />
            </button>
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button text-white rounded-xl hover:text-teal-400 disabled:opacity-50 text-xs font-medium"
              title="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 p-2 sm:px-4 sm:py-2 glass-button-active text-white rounded-xl text-xs font-medium"
              title="Add Customer"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add Customer</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {filtered.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-white/10 rounded-2xl p-5 mb-4">
              <Database size={32} className="text-white" />
            </div>
            <h3 className="text-sm font-semibold text-white mb-1">
              {search ? 'No matching customers' : 'No customers yet'}
            </h3>
            <p className="text-xs text-white mb-5">
              {search ? 'Try a different search term' : 'Add customers to the global database'}
            </p>
            {!search && (
              <button
                onClick={() => setShowAdd(true)}
                className="flex items-center gap-1.5 px-4 py-2 glass-button-active text-white rounded-xl text-xs font-medium"
              >
                <Plus size={14} />
                Add First Customer
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(({ customer, pools, totalCharged, totalRemaining }) => (
              <div
                key={customer.id}
                className="glass-panel rounded-2xl overflow-hidden cursor-pointer hover:ring-1 hover:ring-teal-200 transition-all duration-200"
                onClick={() => setDetailCustomer(customer)}
              >
                <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-white/10/80">
                  <div className="flex items-center gap-3 min-w-0">
                    <h3 className="text-sm font-bold text-white">{customer.name}</h3>
                    {customer.email && (
                      <span className="hidden sm:inline text-[10px] text-white truncate max-w-[140px]">{customer.email}</span>
                    )}
                    <div className="flex gap-1">
                      {customer.owned_cards.map((card, i) => {
                        const color = getCardBrandColor(card.type);
                        return (
                          <div
                            key={i}
                            className={`w-2.5 h-2.5 rounded-full ${color.dot}`}
                            title={card.type}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {totalCharged > 0 && (
                      <div className="hidden sm:flex items-center gap-3 text-[11px]">
                        <span className="text-white">Charged: <span className="font-semibold text-white">${totalCharged.toFixed(2)}</span></span>
                        <span className={`font-bold ${totalRemaining >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                          ${totalRemaining.toFixed(2)} left
                        </span>
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditCustomer(customer); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-teal-400 bg-teal-500/10 rounded-lg glass-hover-teal"
                    >
                      <Pencil size={11} />
                      Edit
                    </button>
                  </div>
                </div>

                {customer.owned_cards.length === 0 ? (
                  <div className="px-4 sm:px-5 py-4">
                    <p className="text-xs text-white italic">No cards assigned</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-4 sm:px-5 py-3.5">
                    {pools.map(pool => {
                      const pct = pool.limit > 0 ? Math.min(100, (pool.charged / pool.limit) * 100) : 0;
                      const barColor = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-teal-500';
                      return (
                        <div key={pool.pool}>
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="flex -space-x-1">
                              {pool.brands.map(b => {
                                const color = getCardBrandColor(b);
                                return <div key={b} className={`w-2 h-2 rounded-full ring-1 ring-white ${color.dot}`} />;
                              })}
                            </div>
                            <span className="text-[10px] font-semibold text-white uppercase tracking-wider">
                              {POOL_LABELS[pool.pool] || pool.pool}
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-1 text-center mb-2">
                            <div>
                              <p className="text-[10px] text-white">Limit</p>
                              <p className="text-xs font-bold text-white">${pool.limit.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-white">Charged</p>
                              <p className="text-xs font-bold text-white">${pool.charged.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-white">Remaining</p>
                              <p className={`text-xs font-bold ${pool.remaining >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                                ${pool.remaining.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          <div className="bg-white/10 rounded-full h-1.5 overflow-hidden mb-2">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>

                          {pool.chargesByCompany.length > 0 ? (
                            <div className="space-y-1">
                              {pool.chargesByCompany.map(({ company, amount }) => (
                                <div key={company} className="flex items-center justify-between text-[10px]">
                                  <span className="text-white truncate mr-2">{company}</span>
                                  <span className="font-medium text-white shrink-0">${amount.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[10px] text-white italic">No charges this month</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {totalCharged > 0 && (
                  <div className="sm:hidden px-4 py-2.5 border-t border-white/10/80 flex items-center justify-between text-[11px]">
                    <span className="text-white">Charged: <span className="font-semibold text-white">${totalCharged.toFixed(2)}</span></span>
                    <span className={`font-bold ${totalRemaining >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                      ${totalRemaining.toFixed(2)} left
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddGlobalCustomerModal
          onClose={() => setShowAdd(false)}
          onSave={handleAdd}
        />
      )}

      {editCustomer && (
        <EditGlobalCustomerModal
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
          onSave={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {detailCustomer && (
        <CustomerDetailModal
          customer={detailCustomer}
          onClose={() => setDetailCustomer(null)}
          onEdit={() => { setDetailCustomer(null); setEditCustomer(detailCustomer); }}
        />
      )}

      {monthDropdownOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl w-full max-w-sm max-h-[50vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <h2 className="text-base font-bold text-white">Select Month</h2>
              <button onClick={() => setMonthDropdownOpen(false)} className="p-1.5 rounded-lg glass-hover">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {yearOptions.map(y => (
                <div key={y}>
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-2 pt-3 pb-1 ${y === selectedYear ? 'text-teal-400' : 'text-white/50'}`}>{y}</p>
                  {MONTHS.map(m => {
                    const my = `${y}-${String(m).padStart(2, '0')}`;
                    const isActive = my === monthYear;
                    return (
                      <button
                        key={my}
                        onClick={() => { selectMonth({ year: y, month: m }); setMonthDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 rounded-xl text-sm mb-0.5 transition-all duration-150 ${
                          isActive ? 'bg-teal-500/10 text-teal-400 font-semibold' : 'text-white glass-hover'
                        }`}
                      >
                        {getMonthName(m)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
